import { useTranslation } from 'react-i18next';
import { cn } from '@/components/ui/cn';
import { LodestarMark, type LodestarMarkProps } from './LodestarMark';

export interface LodestarLogoProps {
  /** Mark size in CSS pixels (wordmark size derives from this). */
  size?: number;
  /** Visual variant — passes through to LodestarMark. */
  variant?: LodestarMarkProps['variant'];
  /** Show the "Relocation Atlas" tagline under the wordmark. */
  withTagline?: boolean;
  /** Render mark only (no wordmark). */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Lodestar lockup — brand mark + wordmark + optional tagline.
 *
 * Two compositions:
 *   - `iconOnly`: mark in isolation (used in tight UI like AI bubble, FAB).
 *   - Default:  mark + Source-Serif wordmark side-by-side (header, footer).
 *
 * Use this for any brand surface — never re-hand-roll the wordmark or mark
 * inline, so a future logo change updates everywhere at once.
 */
export function LodestarLogo({
  size = 28,
  variant = 'default',
  withTagline = false,
  iconOnly = false,
  className,
}: LodestarLogoProps) {
  const { t } = useTranslation();
  if (iconOnly) {
    return <LodestarMark size={size} variant={variant} className={className} />;
  }

  // Wordmark sizing: roughly 1.1× the mark size for visual balance.
  const wordmarkSize = Math.round(size * 1.05);
  const taglineSize = Math.max(9, Math.round(size * 0.38));

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <LodestarMark size={size} variant={variant} />
      <span className="flex flex-col leading-none">
        <span
          className="font-display font-semibold tracking-tight text-ink-900"
          style={{ fontSize: `${wordmarkSize}px` }}
        >
          Lodestar
        </span>
        {withTagline && (
          <span
            className="mt-1 font-mono uppercase tracking-[0.18em] text-ink-500"
            style={{ fontSize: `${taglineSize}px` }}
          >
            {t('brand.tagline', { defaultValue: 'Relocation Atlas' })}
          </span>
        )}
      </span>
    </span>
  );
}
